import React, { createContext, useContext, useMemo, useState } from 'react';

const AuthContext = createContext();

// Credenciais de demonstração para validação de acesso.
const USUARIOS_VALIDOS = [
  {
    login: 'admin',
    senha: 'Admin@123',
    nome: 'Administrador'
  }
];

/**
 * Valida formato mínimo do login.
 * Regras: obrigatório, 3-30 caracteres, letras/números e . _ -
 */
const validarLogin = (login) => {
  const loginNormalizado = String(login || '').trim();

  if (!loginNormalizado) {
    return 'Informe o login.';
  }

  if (loginNormalizado.length < 3 || loginNormalizado.length > 30) {
    return 'O login deve ter entre 3 e 30 caracteres.';
  }

  if (!/^[a-zA-Z0-9._-]+$/.test(loginNormalizado)) {
    return 'Use apenas letras, números, ponto, underscore e hífen.';
  }

  return '';
};

/**
 * Valida segurança mínima da senha.
 * Regras: mínimo 8 caracteres, com maiúscula, minúscula e número.
 */
const validarSenha = (senha) => {
  const senhaNormalizada = String(senha || '');

  if (!senhaNormalizada) {
    return 'Informe a senha.';
  }

  if (senhaNormalizada.length < 8) {
    return 'A senha deve ter pelo menos 8 caracteres.';
  }

  if (!/[A-Z]/.test(senhaNormalizada)) {
    return 'A senha deve conter ao menos uma letra maiúscula.';
  }

  if (!/[a-z]/.test(senhaNormalizada)) {
    return 'A senha deve conter ao menos uma letra minúscula.';
  }

  if (!/[0-9]/.test(senhaNormalizada)) {
    return 'A senha deve conter ao menos um número.';
  }

  return '';
};

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(() => {
    const salvo = localStorage.getItem('usuarioAutenticado');
    return salvo ? JSON.parse(salvo) : null;
  });

  /**
   * Executa validação de login/senha e autentica usuário.
   */
  const autenticar = (login, senha) => {
    const erroLogin = validarLogin(login);
    if (erroLogin) {
      return { ok: false, mensagem: erroLogin };
    }

    const erroSenha = validarSenha(senha);
    if (erroSenha) {
      return { ok: false, mensagem: erroSenha };
    }

    const loginNormalizado = login.trim();
    const usuarioEncontrado = USUARIOS_VALIDOS.find(
      (item) => item.login === loginNormalizado && item.senha === senha
    );

    if (!usuarioEncontrado) {
      return { ok: false, mensagem: 'Login ou senha inválidos.' };
    }

    const sessao = {
      login: usuarioEncontrado.login,
      nome: usuarioEncontrado.nome
    };

    setUsuario(sessao);
    localStorage.setItem('usuarioAutenticado', JSON.stringify(sessao));

    return { ok: true, usuario: sessao };
  };

  const sair = () => {
    setUsuario(null);
    localStorage.removeItem('usuarioAutenticado');
  };

  const value = useMemo(
    () => ({
      usuario,
      autenticado: Boolean(usuario),
      autenticar,
      sair
    }),
    [usuario]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }

  return context;
};
